import React from 'react';
import './BadgeIcons.css';

/** Racing beer mug — fastest guess */
export function BadgeIconFastest({ className = '' }) {
  return (
    <svg
      className={`badge-icon ${className}`.trim()}
      viewBox="0 0 64 64"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M6 22h10M4 30h12M7 38h9"
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
      <path
        d="M22 20h20a2 2 0 0 1 2 2v24a6 6 0 0 1-6 6H26a6 6 0 0 1-6-6V22a2 2 0 0 1 2-2z"
        fill="var(--badge-foam, #F7F4EE)"
      />
      <path
        d="M22 30h20v16a4 4 0 0 1-4 4H26a4 4 0 0 1-4-4V30z"
        fill="var(--badge-ale, #D7A35A)"
      />
      <path
        d="M20.5 20c0-3.8 3-6.8 7.4-6.8 1.5 0 2.9.4 4 1.1A6.5 6.5 0 0 1 38 13.5c4 0 7.4 2.7 7.4 6.1V20H20.5z"
        fill="#fff"
      />
      <path
        d="M44 28h3a5 5 0 0 1 0 10h-3"
        fill="none"
        stroke="var(--badge-foam, #F7F4EE)"
        strokeWidth="3.2"
        strokeLinecap="round"
      />
      <circle cx="52" cy="18" r="3" fill="var(--accent, #D7A35A)" opacity="0.85" />
    </svg>
  );
}

/** Loyalty card fully stamped — never missed a round */
export function BadgeIconPerfect({ className = '' }) {
  return (
    <svg
      className={`badge-icon ${className}`.trim()}
      viewBox="0 0 64 64"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
    >
      <rect
        x="8"
        y="12"
        width="48"
        height="40"
        rx="6"
        fill="var(--badge-foam, #F7F4EE)"
      />
      <rect x="12" y="16" width="40" height="8" rx="2" fill="#173662" opacity="0.85" />
      <text
        x="32"
        y="22.5"
        textAnchor="middle"
        fill="#F7F4EE"
        fontSize="6"
        fontWeight="800"
        fontFamily="var(--font-display, Georgia, serif)"
      >
        7/7
      </text>
      {[0, 1, 2, 3, 4, 5, 6].map((i) => {
        const x = 14 + (i % 4) * 10;
        const y = 30 + Math.floor(i / 4) * 10;
        return (
          <g key={i}>
            <circle cx={x + 3} cy={y + 3} r="4" fill="var(--badge-ale, #D7A35A)" />
            <path
              d={`M${x + 1.2} ${y + 3} l1.5 1.5 l3-3`}
              fill="none"
              stroke="#173662"
              strokeWidth="1.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </g>
        );
      })}
      {/* tipsy smile stamp */}
      <circle cx="50" cy="48" r="7" fill="#173662" />
      <circle cx="47.5" cy="46.5" r="1" fill="#F7F4EE" />
      <circle cx="52.5" cy="46.5" r="1" fill="#F7F4EE" />
      <path
        d="M47 50c1.2 1.6 3.5 1.6 5 0"
        fill="none"
        stroke="#F7F4EE"
        strokeWidth="1.3"
        strokeLinecap="round"
      />
    </svg>
  );
}

/** Bottles + medals for 1st / 2nd / 3rd */
export function BadgeIconPodium({ className = '', place = null }) {
  const bottles = [
    { x: 12, y: 8, h: 34, medal: '#C0C0C0', label: '2', opacity: 0.9 },
    { x: 32, y: 2, h: 40, medal: '#E6C35C', label: '1', opacity: 1 },
    { x: 52, y: 10, h: 32, medal: '#C47A4A', label: '3', opacity: 0.85 }
  ];

  return (
    <svg
      className={`badge-icon ${className}`.trim()}
      viewBox="0 0 64 64"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
    >
      {bottles.map((b) => {
        const bodyTop = b.y + 14;
        const bodyBottom = b.y + b.h;
        return (
          <g key={b.label} opacity={b.opacity}>
            {/* foil / cap */}
            <rect
              x={b.x - 2.5}
              y={b.y}
              width="5"
              height="4"
              rx="1"
              fill="#8B5A2B"
            />
            {/* narrow neck */}
            <rect
              x={b.x - 2}
              y={b.y + 4}
              width="4"
              height="7"
              fill="var(--badge-foam, #F7F4EE)"
            />
            {/* shoulders → body */}
            <path
              d={`
                M ${b.x - 2} ${b.y + 11}
                L ${b.x - 7} ${bodyTop + 3}
                L ${b.x - 7} ${bodyBottom}
                Q ${b.x - 7} ${bodyBottom + 3} ${b.x} ${bodyBottom + 3}
                Q ${b.x + 7} ${bodyBottom + 3} ${b.x + 7} ${bodyBottom}
                L ${b.x + 7} ${bodyTop + 3}
                L ${b.x + 2} ${b.y + 11}
                Z
              `}
              fill="var(--badge-ale, #D7A35A)"
            />
            {/* label band on bottle */}
            <rect
              x={b.x - 5.5}
              y={bodyTop + 8}
              width="11"
              height="7"
              rx="1"
              fill="var(--badge-foam, #F7F4EE)"
              opacity="0.92"
            />
            {/* medal hanging on neck */}
            <path
              d={`M ${b.x} ${b.y + 8} L ${b.x} ${bodyBottom + 2}`}
              stroke="#173662"
              strokeWidth="1.2"
              opacity="0.35"
            />
            <circle
              cx={b.x}
              cy={bodyBottom + 6}
              r="6.5"
              fill={b.medal}
              stroke="#173662"
              strokeWidth="1.2"
            />
            <text
              x={b.x}
              y={bodyBottom + 9}
              textAnchor="middle"
              fill="#173662"
              fontSize="8"
              fontWeight="800"
              fontFamily="var(--font-display, Georgia, serif)"
            >
              {place && String(place) === b.label ? place : b.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

/** Single medal for weekly 1st / 2nd / 3rd */
export function BadgeIconMedal({ className = '', place = 1 }) {
  const colors = {
    1: '#E6C35C',
    2: '#C0C0C0',
    3: '#C47A4A'
  };
  const fill = colors[place] || colors[1];

  return (
    <svg
      className={`badge-icon ${className}`.trim()}
      viewBox="0 0 64 64"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M22 8h20l-3 14H25L22 8z"
        fill="var(--badge-foam, #F7F4EE)"
      />
      <path d="M28 8v10M36 8v10" stroke="#173662" strokeWidth="2" strokeLinecap="round" />
      <circle cx="32" cy="38" r="16" fill={fill} stroke="#173662" strokeWidth="2" />
      <circle cx="32" cy="38" r="11" fill="none" stroke="#173662" strokeWidth="1.2" opacity="0.35" />
      <text
        x="32"
        y="44"
        textAnchor="middle"
        fill="#173662"
        fontSize="18"
        fontWeight="800"
        fontFamily="var(--font-display, Georgia, serif)"
      >
        {place}
      </text>
    </svg>
  );
}

const LABEL = {
  fastest: 'Самые Быстрые',
  perfect: 'Завсегдатаи',
  podium: 'Самые Гениальные',
  podium1: 'Гений · 1',
  podium2: 'Гений · 2',
  podium3: 'Гений · 3',
  fastest1: 'Скорость · 1',
  fastest2: 'Скорость · 2',
  fastest3: 'Скорость · 3'
};

export function badgeLabel(category) {
  return LABEL[category] || category;
}

export function BadgeIcon({ category, place = null, className = '' }) {
  if (category === 'fastest') return <BadgeIconFastest className={className} />;
  if (category === 'perfect') return <BadgeIconPerfect className={className} />;
  if (category === 'podium1' || category === 'fastest1') {
    return <BadgeIconMedal className={className} place={1} />;
  }
  if (category === 'podium2' || category === 'fastest2') {
    return <BadgeIconMedal className={className} place={2} />;
  }
  if (category === 'podium3' || category === 'fastest3') {
    return <BadgeIconMedal className={className} place={3} />;
  }
  if (category === 'podium') {
    return <BadgeIconPodium className={className} place={place} />;
  }
  return null;
}

export default BadgeIcon;
